import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { styled } from "@puzzle/theme";
import intersection from "lodash/intersection";
import Analytics from "lib/analytics/analytics";
import { FeatureFlag, isPosthogFeatureFlagEnabled } from "lib/analytics/featureFlags";
import { useAppRouter } from "lib/useAppRouter";
import { CoaType, MetricCard } from "graphql/types";
import { SpotlightTourProvider } from "components/dashboard/Dashboard/SpotlightTour/SpotlightTourContext";
import { ReportsContainer } from "components/dashboard/Report/ReportsContainer";
import { ReportsContainerWithStatus } from "components/dashboard/Report/ReportsContainerWithStatus";
import { ChartTile } from "components/dashboard/Dashboard/ChartTile/ChartTile";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { useGetDashboardChartDataQuery } from "components/dashboard/Dashboard/ChartTile/graphql.generated";
import { StatCards } from "components/dashboard/Dashboard/StatCards";
import { LaunchpadBanner } from "components/launchpad/LaunchpadBanner";
import { useOnboardingProgress } from "components/launchpad/useOnboardingProgress";
import { useFinancialInstitutions } from "components/integrations/shared/FinancialInstitutionsProvider";
import { useGetMetricCardPreferencesQuery } from "./graphql.generated";
import { FinancialInsights } from "./FinancialInsights";
import { useUpsertMessageSettingsMutation } from "components/users/graphql.generated";
import { useSelf } from "components/users/useSelf";

const DynamicBookDemoModal = dynamic(() =>
  import("components/intro/onboardingFunnelSteps/BookDemoModal/BookDemoModal").then(
    (mod) => mod.BookDemoModal
  )
);

const ICP_WELCOME_MODAL_SEEN = "pz:icp-welcome-modal-seen";

const Wrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "$2",
});

export const DEFAULT_METRICS = [
  MetricCard.Cash,
  MetricCard.TotalExpenses,
  MetricCard.Revenue,
  MetricCard.GrossProfitMargin,
  MetricCard.AccountsPayable,
  MetricCard.AccountsReceivable,
  MetricCard.Spend,
  MetricCard.NetBurn,
  MetricCard.NetBurnMoM,
  MetricCard.Runway,
  MetricCard.NetProfitMargin,
];

export type MetricCardStatus = Record<MetricCard, boolean>;

const CARDS_USING_GET_DASHBOARD_CHART_DATA = [
  MetricCard.TotalExpenses,
  MetricCard.Revenue,
  MetricCard.Spend,
  MetricCard.NetBurnMoM,
];

export const Dashboard = () => {
  const { company, isICPCompany } = useActiveCompany<true>();
  const { showLaunchpadBanner } = useOnboardingProgress();
  const { accounts } = useFinancialInstitutions();
  const [upsertMessageSettings] = useUpsertMessageSettingsMutation();
  const { messageSettings, loading: messageSettingsLoading } = useSelf();
  const { router } = useAppRouter();
  const [isBookDemoModalOpen, setIsBookDemoModalOpen] = useState(true);
  const fromOnboarding = (router.query.source as string) === "intro";
  const isBookDemoModalFeatureFlagEnabled = isPosthogFeatureFlagEnabled(
    FeatureFlag.OnboardingTryDemoModal
  );
  const showBookDemoModal =
    fromOnboarding &&
    isICPCompany &&
    !messageSettingsLoading &&
    !messageSettings?.[ICP_WELCOME_MODAL_SEEN] &&
    isBookDemoModalOpen &&
    !!isBookDemoModalFeatureFlagEnabled;

  const [metricCards, setMetricCards] = useState<MetricCard[]>([]);
  const [metricCardStatus, setMetricCardStatus] = useState<MetricCardStatus>();
  const enabledMetricCards = metricCards.filter((metricCard) => metricCardStatus?.[metricCard]);
  const skipDashboardChartData =
    accounts.length === 0 ||
    // Skip the query if cards that use it are disabled
    intersection(enabledMetricCards, CARDS_USING_GET_DASHBOARD_CHART_DATA).length === 0;
  const { data: dashboardChartData, loading: dashboardChartsLoading } =
    useGetDashboardChartDataQuery({
      variables: {
        companyId: company.id,
      },
      context: {
        batch: false,
      },
      skip: skipDashboardChartData,
    });

  const { data: metricCardPreferencesData } = useGetMetricCardPreferencesQuery({
    variables: {
      companyId: company.id,
    },
    fetchPolicy: "cache-and-network",
  });

  const showSecondRowTiles = !!company?.features.metricCardsAdditional;
  const chartsLoading = !!dashboardChartsLoading && !dashboardChartData;
  const hasDecimalCoaType = company?.coaType === CoaType.Decimal; // is a Decimal company
  // This is used to switch between old and new graph set.
  const showDecimalSecondaryDashboardCharts = isPosthogFeatureFlagEnabled(
    FeatureFlag.DecimalSecondaryDashboardCharts
  );

  const decimalMetricCards = showDecimalSecondaryDashboardCharts
    ? DEFAULT_METRICS
    : [MetricCard.Cash, MetricCard.Spend, MetricCard.Revenue];

  const isDashboardCustomizeButtonEnabled = isPosthogFeatureFlagEnabled(
    FeatureFlag.DashboardCustomizeButton
  );

  const handleCloseBookDemoModal = () => {
    upsertMessageSettings({
      variables: {
        input: {
          key: ICP_WELCOME_MODAL_SEEN,
          value: true,
        },
      },
    });
    setIsBookDemoModalOpen(false);
  };

  useEffect(() => {
    Analytics.dashboardViewed();
  }, []);

  useEffect(() => {
    if (showBookDemoModal) {
      Analytics.welcomeModalDisplayed({ audience: "ICP Company" });
    }
  }, [showBookDemoModal]);

  useEffect(() => {
    if (metricCardPreferencesData) {
      setMetricCardStatus(
        (metricCardPreferencesData?.getMetricCardPreferences.metricCardPreferences || []).reduce(
          (acc, preference) => {
            acc[preference.metricCard] = preference.enabled;
            return acc;
          },
          {} as MetricCardStatus
        )
      );
      setMetricCards(
        (hasDecimalCoaType ? decimalMetricCards : DEFAULT_METRICS).sort((a, b) => {
          const orderMap = new Map(
            metricCardPreferencesData?.getMetricCardPreferences.metricCardPreferences.map(
              (item, index) => [item.metricCard, index]
            )
          );
          const indexA = orderMap.get(a) ?? -1;
          const indexB = orderMap.get(b) ?? -1;
          return indexA - indexB;
        })
      );
    }
  }, [metricCardPreferencesData?.getMetricCardPreferences.metricCardPreferences]);

  return (
    <Wrapper>
      {showBookDemoModal && (
        <DynamicBookDemoModal
          open={showBookDemoModal}
          onClose={() => handleCloseBookDemoModal()}
          analyticsEventLocation="ICP Welcome Modal"
        />
      )}
      {showLaunchpadBanner && <LaunchpadBanner />}
      {isDashboardCustomizeButtonEnabled || hasDecimalCoaType ? (
        <FinancialInsights
          charts={dashboardChartData?.company?.charts || undefined}
          metricCards={metricCards}
          metricCardStatus={metricCardStatus}
          chartsLoading={chartsLoading}
          onChangeStatus={setMetricCardStatus}
          onChangeOrder={setMetricCards}
        />
      ) : (
        <StatCards
          charts={dashboardChartData?.company?.charts || undefined}
          chartsLoading={chartsLoading}
        />
      )}
      {!showSecondRowTiles && (
        <ChartTile
          charts={dashboardChartData?.company?.charts || undefined}
          loading={chartsLoading}
        />
      )}
      <SpotlightTourProvider>
        {isPosthogFeatureFlagEnabled(FeatureFlag.DashboardFsReportsWithStatus) ? (
          <ReportsContainerWithStatus />
        ) : (
          <ReportsContainer />
        )}
      </SpotlightTourProvider>
    </Wrapper>
  );
};
